import { useEffect, useMemo, useRef, useState } from 'react';
import type { CrapsRoll, RoomState } from '@tv-casino/shared';

/**
 * The throw, as data. A bubble-craps machine blows the dice up off the bed and
 * they clatter around the dome before they settle, so the faces on screen have
 * to keep changing for as long as the dice are in the air — and none of those
 * faces may be the one the server rolled, or the dome spoils its own result.
 *
 * The server publishes the roll and its settlements in the same breath, so the
 * animation's only job is to hold the *reading* back until the dice land. What
 * the dice did is never in question here; when it is shown is.
 */

/** How long the dice are in the air before they read as landed. */
export const DICE_THROW_MS = 1400;

/** One face per frame while they tumble: fast enough to blur, slow enough to read as dice. */
export const DICE_FRAME_MS = 70;

const FRAME_COUNT = Math.ceil(DICE_THROW_MS / DICE_FRAME_MS);

/** A tiny deterministic hash, so the same roll always tumbles the same way. */
const hash = (a: number, b: number, c: number): number => {
  let h = (Math.imul(a, 374761393) + Math.imul(b, 668265263) + Math.imul(c, 2246822519)) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
};

/**
 * The faces one die shows, frame by frame, across a whole throw. Every step is
 * 1..5 faces along a six-face cycle, so no frame ever repeats the one before
 * it — a die that shows the same pip twice running reads as a freeze, not a
 * tumble. The sequence is seeded off the roll *number*, never the roll, so
 * nothing in flight can leak what the dice are about to say.
 */
export function tumbleFaces(rollNumber: number, die: number, count = FRAME_COUNT): number[] {
  const out: number[] = [];
  let face = hash(rollNumber, die, 0) % 6;
  for (let i = 0; i < count; i += 1) {
    out.push(face + 1);
    face = (face + 1 + (hash(rollNumber, die, i + 1) % 5)) % 6;
  }
  return out;
}

/** The pair in the air on one frame, clamped so a slow tick cannot run off the end. */
export function tumblePair(rollNumber: number, frame: number): CrapsRoll {
  const at = Math.max(0, Math.min(frame, FRAME_COUNT - 1));
  return [tumbleFaces(rollNumber, 0)[at]!, tumbleFaces(rollNumber, 1)[at]!];
}

/** Nobody gets thrown dice they did not ask for: honour the OS switch. Read
 * off `globalThis` so the package stays DOM-free — a native shell wires its
 * own accessibility switch in later. */
const stillPreferred = (): boolean => {
  const mq = (globalThis as { matchMedia?: (query: string) => { matches: boolean } }).matchMedia;
  return typeof mq === 'function' && mq('(prefers-reduced-motion: reduce)').matches;
};

interface Flight {
  /** The frame the dice are on, or null once they have landed. */
  frame: number | null;
  /** The roll the dice are in flight for — the counter that armed the clock. */
  flight: number;
}

/**
 * The flight clock, shared by every surface that reads a roll: armed when the
 * published counter moves under us, landing DICE_THROW_MS later. Mounting
 * mid-hand does not arm it — only a counter that moves does — and the OS
 * reduced-motion switch skips the flight entirely. The TV and the phone each
 * run their own clock off the same counter, so neither can be in the air when
 * the other has landed. `tick` false skips the per-frame counter: a reader
 * that only needs "in the air or not" re-renders twice per throw, not twenty.
 */
function useFlight(rollNumber: number, tick = true): Flight {
  const [frame, setFrame] = useState<number | null>(null);
  const seen = useRef(rollNumber);
  const flight = useRef(rollNumber);

  useEffect(() => {
    if (rollNumber === seen.current) return;
    seen.current = rollNumber;
    if (rollNumber <= 0 || stillPreferred()) return;
    flight.current = rollNumber;
    setFrame(0);
    const frames = tick ? setInterval(() => setFrame((f) => (f ?? 0) + 1), DICE_FRAME_MS) : null;
    const land = setTimeout(() => {
      if (frames) clearInterval(frames);
      setFrame(null);
    }, DICE_THROW_MS);
    return () => {
      if (frames) clearInterval(frames);
      clearTimeout(land);
      setFrame(null);
    };
  }, [rollNumber, tick]);

  // The render that first carries a new counter paints before the effect above
  // can arm the clock, so it is already a throw: without this, one frame of the
  // landed roll — its faces, its red sweeps — flashes up ahead of the tumble.
  if (rollNumber !== seen.current && rollNumber > 0 && !stillPreferred()) {
    return { frame: 0, flight: rollNumber };
  }
  return { frame, flight: flight.current };
}

export interface CrapsThrow {
  /** What to draw on the dice right now: the tumble in flight, the roll once landed. */
  faces: CrapsRoll | null;
  /** True while the dice are still in the air — the call and the total wait on this. */
  airborne: boolean;
}

/**
 * Drive one throw off the published roll counter. The counter moving is the
 * whole trigger: the TV and the phone each run their own dome from the same
 * number, so neither can animate a roll the server did not make.
 */
export function useCrapsThrow(rollNumber: number, dice: CrapsRoll | null): CrapsThrow {
  const { frame, flight } = useFlight(rollNumber);
  const airborne = frame !== null;
  const tumbling = useMemo(
    () => (frame === null ? null : tumblePair(flight, frame)),
    [frame, flight],
  );

  return { faces: tumbling ?? dice, airborne };
}

/**
 * The table's reading of a roll, held for the flight. A roll and everything it
 * decided — the settlements, the sweeps, the newest bead on the ribbon — arrive
 * in one state push, but none of it may show while the dice are still tumbling.
 * While they are in the air the reading is the world as it stood before the
 * throw: only the counter, the dice and the call pass through, so the dome
 * still throws and still lands on the real roll.
 *
 * The whole room is held, not just the craps slice. A roll that ends the hand
 * — a seven-out, a craps on the come-out, a made point — flips the phase to
 * payout and marks every seat done in that same push, and a felt that read
 * those early would shut, drop its chips and skip the scroll to the dome,
 * calling the result before the dice have said it.
 */
export function useCrapsRoomReading(state: RoomState | null): RoomState | null {
  const rollNumber = state?.craps?.rollNumber ?? 0;
  // Airborne or not is all the reading needs. Ticking the frame here would
  // re-render the whole route every DICE_FRAME_MS for the length of a throw.
  const { frame } = useFlight(rollNumber, false);
  const latest = useRef({ roll: rollNumber, state });
  const preRoll = useRef(state);

  // The render that first carries a new counter is the last chance to remember
  // the world the dice were thrown into.
  if (latest.current.roll !== rollNumber) {
    preRoll.current = latest.current.state;
    latest.current.roll = rollNumber;
  }
  latest.current.state = state;

  const pre = preRoll.current;
  const held =
    frame !== null && state?.craps != null && pre?.craps != null && pre.craps.rollNumber < rollNumber;
  return useMemo(() => {
    if (!held || !state?.craps || !pre?.craps) return state;
    return {
      ...pre,
      craps: {
        ...pre.craps,
        rollNumber: state.craps.rollNumber,
        lastRoll: state.craps.lastRoll,
        lastEvent: state.craps.lastEvent,
        // The last roll's win and loss marks come off while the dice are up,
        // so the felt flashes again on landing even when a spot settles the
        // same way twice running.
        lastSettlements: [],
      },
    };
  }, [held, state, pre]);
}
